// src/pages/admin/AdminTablePage.js
import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Pagination from '../../components/Pagination';
import tableLabels from './TableLabels';
import UserInfo from '../../components/UserInfo';
import './AdminTablePage.css';
import { GlobalContext } from '../../contexts/GlobalContext';

function AdminTablePage() {
    const { tableName } = useParams();
    const [records, setRecords] = useState([]);
    const [selectedRecords, setSelectedRecords] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const { resultsPerPage, setResultsPerPage } = useContext(GlobalContext);

    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        fetchRecords(currentPage, resultsPerPage);
    }, [currentPage, resultsPerPage]);

    // useEffect(() => {
    //     fetchRecords(currentPage);
    // }, [currentPage]);

    const fetchRecords = async (page) => {
        try {
            setIsLoading(true);
            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/api/admin/${tableName}?page=${page}&limit=10`,
                {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );
    
            if (!response.ok) {
                if (response.status === 401) {
                    // Redirect to login page or show an error message
                    navigate('/login');
                    return;
                }
                const errorData = await response.json();
                throw new Error(errorData.error || 'An error occurred while fetching records.');
            }
    
            const data = await response.json();
            setRecords(data.records);
            setTotalPages(data.totalPages);
            setCurrentPage(data.currentPage);
        } catch (error) {
            console.error('Error fetching records:', error);
            // Display an error message to the user
        } finally {
            setIsLoading(false);
        }
    };

    const truncate = (str, n) => {
        if (typeof str !== 'string') return str;
        return str.length > n ? str.substring(0, n) + '...' : str;
    };    

    // Handle selection of records
    const handleSelectRecord = (submission_id) => {
        setSelectedRecords((prevSelected) =>
        prevSelected.includes(submission_id)
            ? prevSelected.filter((id) => id !== submission_id)
            : [...prevSelected, submission_id]
        );
    };

    const handleSelectAll = () => {
        const allIds = records.map((record) => record.submission_id);
        setSelectedRecords(allIds);
    };

    const handleDeselectAll = () => {
        setSelectedRecords([]);
    };

    const handleDeleteSelected = async () => {
        if (selectedRecords.length === 0) return;

        // Show confirmation overlay
        if (!window.confirm('您確定要刪除選擇的資料嗎？')) return;

        try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/${tableName}`,
            {
                method: 'DELETE',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ submission_ids: selectedRecords }),
            }
        );
        const data = await response.json();
        if (response.ok) {
            // Refresh records
            fetchRecords(currentPage);
            setSelectedRecords([]);
            alert('資料已成功刪除。');
        } else {
            alert(`刪除失敗。原因：${data.error}`);
        }
        } catch (error) {
            console.error('Error deleting records:', error);
            alert('刪除過程中發生錯誤。');
        }
    };

    return (
        <div className="page-content">
        <h2><Link to="/admin">管理主頁</Link> ： {tableLabels[tableName]}</h2>
        <UserInfo />
        <div className="admin-actions">
            <button className="button button-m" onClick={() => navigate('add')}>
            新增
            </button>
            {/* <button className="button button-m" onClick={handleSelectAll}>
            選擇所有
            </button>
            <button className="button button-m" onClick={handleDeselectAll}>
            不選擇
            </button> */}
            <button className="button button-m" onClick={handleDeleteSelected}>
            刪除
            </button>
        </div>
        <Pagination
            totalPages={totalPages}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            resultsPerPage={resultsPerPage}
            setResultsPerPage={setResultsPerPage}
        />
        {isLoading ? (
            <p>載入中...</p>
        ) : (
            <table className="admin-table">
            <thead>
                <tr>
                <th></th>
                {/* Render table headers dynamically */}
                {records.length > 0 &&
                    Object.keys(records[0])
                    .filter(
                        (key) =>
                        ![
                            'submission_id',
                            'is_finalized',
                            'ip_address',
                            'last_modified_date',
                            'submission_date',
                            'content',
                            'image1',
                            'image2',
                            'picture',
                            'picture1',
                            'picture2',
                            'comment',
                            'comment1',
                            'comment2',
                            'mp3',
                            'm3u',
                            'remark',
                        ].includes(key)
                    )
                    .map((key) => <th key={key}>{key}</th>)}
                <th>操作</th>
                </tr>
            </thead>
            <tbody>
                {records.map((record) => (
                <tr key={record.submission_id}>
                    <td>
                    <input
                        type="checkbox"
                        checked={selectedRecords.includes(record.submission_id)}
                        onChange={() => handleSelectRecord(record.submission_id)}
                    />
                    </td>
                    {/* Render record data dynamically */}
                    {Object.keys(record)
                    .filter(
                        (key) =>
                        ![
                            'submission_id',
                            'is_finalized',
                            'ip_address',
                            'last_modified_date',
                            'submission_date',
                            'content',
                            'image1',
                            'image2',
                            'picture',
                            'picture1',
                            'picture2',
                            'comment',
                            'comment1',
                            'comment2',
                            'mp3',
                            'm3u',
                            'remark',
                        ].includes(key)
                    )
                    .map((key) => (
                        <td key={key} title={record[key]}>
                            {truncate(record[key], 50)}
                        </td>
                    ))}
                    <td>
                    <button
                        className="button button-m"
                        onClick={() => navigate(`edit/${record.submission_id}`)}
                    >
                        修改
                    </button>
                    <button
                        className="button button-m"
                        onClick={() => {
                        setSelectedRecords([record.submission_id]);
                        handleDeleteSelected();
                        }}
                    >
                        刪除
                    </button>
                    </td>
                </tr>
                ))}
            </tbody>
            </table>
        )}
        <div className="admin-actions">
            {/* Duplicate action buttons at the bottom */}
            <button className="button button-m" onClick={() => navigate('add')}>
                新增
            </button>
            {/* <button className="button button-m" onClick={handleSelectAll}>
                選擇所有
            </button>
            <button className="button button-m" onClick={handleDeselectAll}>
                不選擇
            </button> */}
            <button className="button button-m" onClick={handleDeleteSelected}>
                刪除
            </button>
        </div>
        {/* Pagination */}
        <Pagination
            totalPages={totalPages}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            resultsPerPage={resultsPerPage}
            setResultsPerPage={setResultsPerPage}
        />
        </div>
    );
}

export default AdminTablePage;



